/**
 * Main Application Entry Point
 * Integrates all providers, routing, and core features
 */

import React, { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ReferralBootstrap } from './components/referral/ReferralBootstrap';
import { AgentsProvider, useAgents } from './contexts/AgentsContext';
import { AppearanceProvider } from './contexts/AppearanceContext';
import { NotificationProvider } from './components/notifications/NotificationProvider';
import { Layout } from './components/layout/Layout';
import { Header } from './components/layout/Header';
import { TranslationPage } from './features/translate/TranslationPage';
import { AgentDrawer } from './components/agents/AgentDrawer';
import HomePage from './pages/HomePage';
import LandingPage from './pages/LandingPage';
import { EmailVerificationPending } from './pages/EmailVerificationPending';
import { EmailVerification } from './pages/EmailVerification';
import { OAuthCallbackPage } from './pages/OAuthCallbackPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Dashboard } from './pages/Dashboard';
import { ApiKeysRoute } from './features/package-features/pages/ApiKeysRoute';
import { EmbedWidgetsRoute } from './features/package-features/pages/EmbedWidgetsRoute';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { MarketplacePage } from './pages/MarketplacePage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { PaymentResultPage } from './pages/PaymentResultPage';
import { AgentManagement } from './pages/AgentManagement';
import { CharacteristicManagement } from './pages/CharacteristicManagement';
import { KnowledgeManagement } from './pages/KnowledgeManagement';
import { CustomToolsPage } from './pages/CustomToolsPage';
import { SettingsPage } from './pages/SettingsPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { NotificationsPage } from './features/notifications/pages/NotificationsPage';
import { GenerateVideoPage } from './features/generate-video/pages/GenerateVideoPage';
import { ProjectsPage } from './features/projects/pages/ProjectsPage';
import { ProjectDetailPage } from './features/projects/pages/ProjectDetailPage';
import { ChatScreen } from './pages/ChatScreen';
import { SharedConversationPage } from './pages/SharedConversationPage';
import { DeviceAuthPage } from './pages/DeviceAuthPage';
import NewChatPage from './pages/NewChatPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { BrowserExtensionPrivacyPage } from './pages/privacy/BrowserExtensionPrivacyPage';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import {
  Bot,
  LayoutDashboard,
  Users,
  CreditCard,
  Code,
  Image as ImageIcon,
  KeyRound,
  BookOpen,
  Settings,
  MessageCircleWarning,
  Plug,
  BrainCircuit,
  Store,
  Wrench,
  X,
  Bell,
  Clapperboard,
} from 'lucide-react';
import { CreateAgentInput } from './types';
import { useQuotaErrorHandler } from './hooks/useQuotaErrorHandler';
import { UpgradeModal } from './components/subscription';
import { ConfirmDialog } from './components/common/ConfirmDialog';
import { FeedbackDialog } from './components/feedback/FeedbackDialog';
import { mergeAnonymousSession } from './services/api';
import { ANONYMOUS_PENDING_MERGE_SESSION_STORAGE_KEY } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';

// ============================================
// Root Route: redirects authenticated users to /new-chat
// ============================================

const getTrustedCliDeviceReturnTo = (returnTo: string | null) => {
  if (!returnTo) return null;

  try {
    const parsed = returnTo.startsWith('/')
      ? new URL(returnTo, window.location.origin)
      : new URL(returnTo);

    if (
      parsed.origin === window.location.origin &&
      parsed.pathname === '/device' &&
      parsed.searchParams.has('user_code')
    ) {
      return parsed.pathname + parsed.search;
    }
  } catch {
    return null;
  }

  return null;
};

const RootRoute: React.FC = () => {
  const { isAuthenticated, isAnonymous, isLoading } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white dark:bg-slate-900">
        <div className="text-center">
          <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-teal-500 border-r-transparent"></div>
          <p className="text-gray-600 dark:text-slate-400">{t('app.loading')}</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated && !isAnonymous) {
    const returnTo = getTrustedCliDeviceReturnTo(
      new URLSearchParams(location.search).get('returnTo')
    );
    if (returnTo) {
      return <Navigate to={returnTo} replace />;
    }

    return <Navigate to="/new-chat" replace />;
  }

  return <HomePage />;
};

const ChatScreenRoute: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <ChatScreen key={sessionId ?? 'missing-session'} />;
};

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const SettingsDialog: React.FC<SettingsDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        className: 'max-h-[92vh] rounded-xl bg-gray-50 dark:bg-slate-900',
      }}
    >
      <DialogTitle className="border-b border-gray-200 bg-white px-5 py-4 dark:border-slate-700 dark:bg-slate-800">
        <Box className="flex min-w-0 items-center justify-between gap-4">
          <Box className="flex min-w-0 items-center gap-3">
            <Box className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-500">
              <Settings className="text-white" size={22} />
            </Box>
            <Box className="min-w-0">
              <Typography variant="h6" className="font-semibold text-gray-900 dark:text-white">
                {t('settings.title')}
              </Typography>
              <Typography variant="body2" className="truncate text-gray-500 dark:text-slate-400">
                {t('settings.subtitle')}
              </Typography>
            </Box>
          </Box>
          <IconButton type="button" onClick={onClose} aria-label={t('common.close')}>
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers className="bg-gray-50 p-4 dark:bg-slate-900 md:p-6">
        <SettingsPage variant="modal" />
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// Main App Component (with contexts)
// ============================================

const AppContent: React.FC = () => {
  const { t } = useTranslation();
  const { agents, createAgent } = useAgents();
  const { user, isAnonymous } = useAuth();
  const [isAgentDrawerOpen, setIsAgentDrawerOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [pendingMergeSessionId, setPendingMergeSessionId] = useState<string | null>(null);
  const [isMergingAnonymousSession, setIsMergingAnonymousSession] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string>(
    agents.find((a) => a.isDefault)?.id || agents[0]?.id || ''
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { errorState, closeModal } = useQuotaErrorHandler();

  useEffect(() => {
    const defaultAgentId = agents.find((agent) => agent.isDefault)?.id || agents[0]?.id || '';

    setSelectedAgentId((currentAgentId) => {
      if (currentAgentId && agents.some((agent) => agent.id === currentAgentId)) {
        return currentAgentId;
      }

      return defaultAgentId;
    });
  }, [agents, user?.publicId]);

  const handleCreateAgent = () => {
    setIsAgentDrawerOpen(true);
  };

  const handleCloseAgentDrawer = () => {
    setIsAgentDrawerOpen(false);
  };

  const handleOpenSettings = () => {
    setIsSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
  };

  const handleSaveAgent = async (input: CreateAgentInput) => {
    await createAgent(input);
  };

  useEffect(() => {
    const handlePendingMerge = (event: Event) => {
      const detail = (event as CustomEvent<{ sessionId?: string }>).detail;
      if (detail?.sessionId) {
        setPendingMergeSessionId(detail.sessionId);

        if (location.pathname === `/chat/${detail.sessionId}`) {
          navigate('/new-chat', { replace: true });
        }
      }
    };

    window.addEventListener('anonymous:merge-pending', handlePendingMerge);
    return () => {
      window.removeEventListener('anonymous:merge-pending', handlePendingMerge);
    };
  }, [location.pathname, navigate]);

  useEffect(() => {
    const handleOpenSettingsEvent = () => {
      setIsSettingsOpen(true);
    };

    window.addEventListener('settings:open', handleOpenSettingsEvent);
    return () => {
      window.removeEventListener('settings:open', handleOpenSettingsEvent);
    };
  }, []);

  useEffect(() => {
    if (!user || isAnonymous) {
      return;
    }

    const storedSessionId = window.sessionStorage.getItem(
      ANONYMOUS_PENDING_MERGE_SESSION_STORAGE_KEY
    );
    if (storedSessionId) {
      setPendingMergeSessionId(storedSessionId);
    }
  }, [isAnonymous, user]);

  const handleSkipAnonymousMerge = () => {
    window.sessionStorage.removeItem(ANONYMOUS_PENDING_MERGE_SESSION_STORAGE_KEY);
    setPendingMergeSessionId(null);
  };

  const handleConfirmAnonymousMerge = async () => {
    if (!pendingMergeSessionId) return;

    setIsMergingAnonymousSession(true);
    try {
      const response = await mergeAnonymousSession(pendingMergeSessionId);
      if (!response.success || !response.data) {
        throw new Error(response.error || t('anonymousMerge.error'));
      }

      window.sessionStorage.removeItem(ANONYMOUS_PENDING_MERGE_SESSION_STORAGE_KEY);
      setPendingMergeSessionId(null);
      navigate(`/chat/${response.data.id}`, {
        replace: true,
        state: { initialSession: response.data },
      });
    } catch (error) {
      console.error(t('anonymousMerge.error'), error);
    } finally {
      setIsMergingAnonymousSession(false);
    }
  };

  if (
    user &&
    isAnonymous &&
    location.pathname !== '/' &&
    location.pathname !== '/landing' &&
    !location.pathname.startsWith('/chat/') &&
    !location.pathname.startsWith('/share/') &&
    location.pathname !== '/email-sent' &&
    location.pathname !== '/verify-email' &&
    location.pathname !== '/reset-password' &&
    location.pathname !== '/auth/callback'
  ) {
    return <Navigate to="/" replace />;
  }

  // Determine header content based on route
  const getHeader = () => {
    switch (location.pathname) {
      case '/services/translate':
        return (
          <Header
            title={t('header.translate.title')}
            subtitle={t('header.translate.subtitle')}
            icon={<Bot className="text-white" size={24} />}
          />
        );
      case '/services/generate-video':
        return (
          <Header
            title={t('header.generateVideo.title')}
            subtitle={t('header.generateVideo.subtitle')}
            icon={<Clapperboard className="text-white" size={24} />}
          />
        );
      case '/dashboard':
        return (
          <Header
            title={t('header.dashboard.title')}
            subtitle={t('header.dashboard.subtitle')}
            icon={<LayoutDashboard className="text-white" size={24} />}
          />
        );
      case '/agents':
        return (
          <Header
            title={t('header.agents.title')}
            subtitle={t('header.agents.subtitle')}
            icon={<Users className="text-white" size={24} />}
          />
        );
      case '/characteristics':
        return (
          <Header
            title={t('header.characteristics.title')}
            subtitle={t('header.characteristics.subtitle')}
            icon={<BrainCircuit className="text-white" size={24} />}
          />
        );
      case '/knowledge':
        return (
          <Header
            title={t('header.knowledge.title')}
            subtitle={t('header.knowledge.subtitle')}
            icon={<BookOpen className="text-white" size={24} />}
          />
        );
      case '/tools':
        return (
          <Header
            title={t('header.tools.title', 'Custom Tools')}
            subtitle={t(
              'header.tools.subtitle',
              'Manage custom API tools, OpenAPI imports, and human-in-the-loop policies'
            )}
            icon={<Wrench className="text-white" size={24} />}
          />
        );
      case '/subscription/payment-result':
      case '/subscription':
        return (
          <Header
            title={t('header.subscription.title')}
            subtitle={t('header.subscription.subtitle')}
            icon={<CreditCard className="text-white" size={24} />}
          />
        );
      case '/code':
        return (
          <Header
            title={t('header.code.title')}
            subtitle={t('header.code.subtitle')}
            icon={<Code className="text-white" size={24} />}
          />
        );
      case '/image':
        return (
          <Header
            title={t('header.image.title')}
            subtitle={t('header.image.subtitle')}
            icon={<ImageIcon className="text-white" size={24} />}
          />
        );
      case '/api-keys':
        return (
          <Header
            title={t('header.apiKeys.title')}
            subtitle={t('header.apiKeys.subtitle')}
            icon={<KeyRound className="text-white" size={24} />}
          />
        );
      case '/embed-widgets':
        return (
          <Header
            title="Embed Widgets"
            subtitle="Website chat widgets for published agents"
            icon={<Code className="text-white" size={24} />}
          />
        );
      case '/integrations':
        return (
          <Header
            title="Integrations"
            subtitle="Telegram, Discord, API and webhook channels"
            icon={<Plug className="text-white" size={24} />}
          />
        );
      case '/marketplace':
        return (
          <Header
            title="Skills & Tools"
            subtitle="Private capabilities, public marketplace, installs, and agent bindings"
            icon={<Store className="text-white" size={24} />}
          />
        );
      case '/feedback':
        return (
          <Header
            title={t('header.feedback.title')}
            subtitle={t('header.feedback.subtitle')}
            icon={<MessageCircleWarning className="text-white" size={24} />}
          />
        );
      default:
        if (location.pathname.startsWith('/notifications')) {
          return (
            <Header
              title={t('notifications.title')}
              subtitle={t('notifications.subtitle')}
              icon={<Bell className="text-white" size={24} />}
            />
          );
        }
        // Check if it's a chat route (starts with /chat/)
        if (location.pathname.startsWith('/chat/')) {
          return null; // ChatScreen has its own header
        }
        return null;
    }
  };

  return (
    <>
      <Routes>
        {/* Root: redirects authenticated users to /new-chat, shows home page otherwise */}
        <Route path="/" element={<RootRoute />} />

        {/* Public landing page for product benefits and integrations */}
        <Route path="/landing" element={<LandingPage />} />

        {/* Email Verification Pending - shown after registration (no layout, not protected) */}
        <Route path="/email-sent" element={<EmailVerificationPending />} />

        {/* Email Verification Handler - processes token from email link (no layout, not protected) */}
        <Route path="/verify-email" element={<EmailVerification />} />

        {/* OAuth callback - hydrates auth state after backend sets refresh cookie */}
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />

        {/* Password reset - public link from email */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Public shared conversation snapshot - no private layout */}
        <Route path="/share/:shareToken" element={<SharedConversationPage />} />

        {/* Public privacy policy for the AppAI Browser Debug Chrome extension (linked from the Chrome Web Store) */}
        <Route path="/privacy/browser-extension" element={<BrowserExtensionPrivacyPage />} />

        {/* CLI Device authorization flow */}
        <Route path="/device" element={<DeviceAuthPage />} />

        {/* App routes with layout - Protected */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout header={getHeader()} onOpenSettings={handleOpenSettings}>
                <Routes>
                  {/* Translation Service */}
                  <Route
                    path="/translate"
                    element={<Navigate to="/services/translate" replace />}
                  />
                  <Route
                    path="/services/translate"
                    element={
                      <TranslationPage
                        agents={agents}
                        onCreateAgent={handleCreateAgent}
                        selectedAgentId={selectedAgentId}
                      />
                    }
                  />

                  {/* Generate Video Service (availability/upsell handled by the page) */}
                  <Route path="/services/generate-video" element={<GenerateVideoPage />} />

                  {/* New Chat */}
                  <Route path="/new-chat" element={<NewChatPage />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/projects/:projectId" element={<ProjectDetailPage />} />

                  {/* Dashboard Page */}
                  <Route path="/dashboard" element={<Dashboard />} />

                  {/* API Key Management */}
                  <Route path="/api-keys" element={<ApiKeysRoute />} />
                  <Route path="/embed-widgets" element={<EmbedWidgetsRoute />} />
                  <Route path="/integrations" element={<IntegrationsPage />} />
                  <Route path="/marketplace" element={<MarketplacePage />} />

                  <Route path="/agents" element={<AgentManagement />} />
                  <Route path="/characteristics" element={<CharacteristicManagement />} />
                  <Route path="/knowledge" element={<KnowledgeManagement />} />
                  <Route path="/tools" element={<CustomToolsPage />} />
                  <Route path="/settings" element={<Navigate to="/new-chat" replace />} />
                  <Route path="/feedback" element={<FeedbackPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/notifications/:publicId" element={<NotificationsPage />} />
                  <Route path="/chat/:sessionId" element={<ChatScreenRoute />} />
                  <Route path="/subscription" element={<SubscriptionPage />} />
                  <Route path="/subscription/payment-result" element={<PaymentResultPage />} />
                  <Route
                    path="/code"
                    element={
                      <div className="p-8 text-gray-900 dark:text-slate-100">
                        {t('header.code.title')} - {t('common.comingSoon')}
                      </div>
                    }
                  />
                  <Route
                    path="/image"
                    element={
                      <div className="p-8 text-gray-900 dark:text-slate-100">
                        {t('header.image.title')} - {t('common.comingSoon')}
                      </div>
                    }
                  />

                  {/* 404 Not Found */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>

                {/* Agent Drawer - Global component */}
                <AgentDrawer
                  open={isAgentDrawerOpen}
                  onClose={handleCloseAgentDrawer}
                  onSave={handleSaveAgent}
                />
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>

      <SettingsDialog open={isSettingsOpen} onClose={handleCloseSettings} />

      {/* Global Upgrade Modal - Triggered by quota errors (suppressed on chat screen in favor of inline banner) */}
      <UpgradeModal
        open={errorState.isOpen && !location.pathname.startsWith('/chat')}
        onClose={closeModal}
        title={errorState.title}
        message={errorState.message}
        upgradeUrl={errorState.upgradeUrl}
        isAnonymousLimit={errorState.isAnonymousLimit}
      />
      <ConfirmDialog
        open={Boolean(pendingMergeSessionId) && Boolean(user) && !isAnonymous}
        title={t('anonymousMerge.title')}
        message={t('anonymousMerge.message')}
        confirmText={t('anonymousMerge.confirm')}
        cancelText={t('anonymousMerge.cancel')}
        loading={isMergingAnonymousSession}
        onClose={handleSkipAnonymousMerge}
        onConfirm={() => void handleConfirmAnonymousMerge()}
      />
      <FeedbackDialog />
      <ReferralBootstrap />
    </>
  );
};

// ============================================
// Root App Component (with all providers)
// ============================================

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <NotificationProvider>
            <AppearanceProvider>
              <AgentsProvider>
                <AppContent />
              </AgentsProvider>
            </AppearanceProvider>
          </NotificationProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
